import { useQuery } from '@tanstack/react-query';

import { getCompaniesFindOptions } from 'client/@tanstack/react-query.gen';
import { Role } from 'client/types.gen';
import type {
  CompanyBasicResponse,
  GetCompaniesFindData,
  GetCompaniesFindResponse,
  GetPeopleCurrentResponse,
  GetTeamResponse,
} from 'client/types.gen';
import { useQueryData, useSearch } from 'helpers/search';
import type { PageItem } from 'types/api';
import type { UseSearch } from 'types/search';

type CompanyType = PageItem<GetCompaniesFindResponse>['type'];

// Acrew is currently our only source_company, and its candidates are
// the only ones that can have its source_company_tags (Custom B4B Client Tags).
export const isAcrew = (company?: CompanyBasicResponse | null) =>
  company && company.id === 41;

export const getCompanyTypeRoles = (type: CompanyType) => {
  switch (type) {
    case 'investor':
      return {
        lead: Role.INVESTOR_TEAM_LEAD,
        member: Role.INVESTOR_TEAM_MEMBER,
      };
    case 'search':
      return {
        lead: Role.SEARCH_TEAM_LEAD,
        member: Role.SEARCH_TEAM_MEMBER,
      };
    case 'corporate':
      return {
        lead: Role.CORPORATE_TEAM_LEAD,
        member: Role.CORPORATE_TEAM_MEMBER,
      };
    default:
      return {};
  }
};

export const isCompanyType = (
  // eslint-disable-next-line @typescript-eslint/default-param-last, default-param-last
  role: Partial<
    Required<GetPeopleCurrentResponse>['roles'][number] | GetTeamResponse
  > = {},
  type: CompanyType | CompanyType[],
) => {
  const companyType = role.company?.type;
  return Array.isArray(type)
    ? type.includes(companyType!)
    : companyType === type;
};

export const useSearchCompanies: UseSearch<
  GetCompaniesFindResponse,
  Omit<GetCompaniesFindData, 'url'>
> = (opts, p) => {
  const { debouncedPhrase, enabled, phrase, ...search } = useSearch(opts);
  const query = useQuery({
    enabled,
    ...getCompaniesFindOptions({
      ...p,
      query: {
        ...p?.query,
        phrase: opts?.debounce ? debouncedPhrase : phrase,
      },
    }),
  });
  const queryDataProps = useQueryData({
    ...opts,
    phrase,
    predicate: (company, value) =>
      company.id === value || company.slug === value,
    query,
  });

  return {
    // eslint-disable-next-line @tanstack/query/no-rest-destructuring
    ...query,
    ...search,
    ...queryDataProps,
    debouncedPhrase,
    phrase,
  };
};
